import clsx from 'clsx';
import DOMPurify from 'dompurify';
import dot from 'dot-object';
import { RadioButton } from 'primereact/radiobutton';
import type { Key } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { MakeRequired } from '../../../common/types/make-required';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonData, JsonDataItem } from '../../generic-types';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';

export declare type Orientation = 'VERTICAL' | 'HORIZONTAL';

export interface RadioGroupResult {
  value: unknown;
  text: string;
}

export interface RadioGroupProps extends MakeRequired<DynamicFieldComponentProps<RadioGroupResult>, 'name'> {
  data?: JsonData;
  textField: string;
  valueField: string;
  orientation?: Orientation;
  archivedField?: string;
  showSelectedArchivedOptions?: boolean; // Feature flag to show selected archived options works in combination with archivedField
}

const createMarkup = (value: string) => {
  return { __html: DOMPurify.sanitize(value, { USE_PROFILES: { html: true } }) };
};

const RadioGroup = ({
  name,
  label,
  value,
  error,
  onChange,
  data,
  textField,
  valueField,
  readOnly,
  required,
  orientation = 'VERTICAL',
  disabled,
  labelStyles,
  archivedField,
  showSelectedArchivedOptions = false,
}: RadioGroupProps) => {
  const [selected, setSelected] = useState<RadioGroupResult>();
  const radioButtonId = useId();
  const initialSelectedValue = useRef<RadioGroupResult | undefined>(undefined);

  useEffect(() => {
    if (value) {
      setSelected(value);
      if (!initialSelectedValue.current) {
        initialSelectedValue.current = value;
      }
    }
  }, [value]);

  const tagCreator = useTestTag(label || '');

  const handleChange = (target: RadioGroupResult) => {
    setSelected(target);
    onChange?.(target);
  };

  const optionLabelStyles = clsx('text-sm', { 'text-readonly': disabled });

  const containerStyles = clsx(
    'flex gap-2',
    { 'rounded-sm border border-error': !readOnly && error },
    { 'flex-wrap': orientation !== 'VERTICAL' },
    { 'flex-col': orientation === 'VERTICAL' }
  );

  const isSelected = (item: JsonDataItem) => {
    return (selected?.value as Key)?.toString() === (item[valueField] as Key).toString();
  };

  const wasInitiallySelected = (item: JsonDataItem): boolean => {
    return (
      !!initialSelectedValue.current &&
      (initialSelectedValue.current.value as Key)?.toString() === (item[valueField] as Key).toString()
    );
  };

  const shouldRenderOption = (item: JsonDataItem): boolean => {
    if (!showSelectedArchivedOptions || !archivedField) {
      return true;
    } else {
      const isArchived = !!dot.pick(archivedField, item);

      if (!isArchived) {
        return true;
      } else {
        return wasInitiallySelected(item);
      }
    }
  };

  const renderItem = (item: JsonDataItem, i: number) => {
    return (
      <div className="flex items-center gap-2 px-1" key={`${radioButtonId}_item_${i}`}>
        <RadioButton
          inputId={`${radioButtonId}_item_${i}`}
          name={name}
          value={item[valueField] as Key}
          checked={isSelected(item)}
          className="odin"
          onChange={() => handleChange({ value: item[valueField], text: item[textField] as string })}
          disabled={disabled || readOnly}
        />
        <label
          htmlFor={`${radioButtonId}_item_${i}`}
          className={optionLabelStyles}
          ref={tagCreator(`${((item[textField] as string) ?? '').replace(/\s+/g, '-').toLowerCase()}-radio-group`)}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: createMarkup sanitises the markup
          dangerouslySetInnerHTML={createMarkup((item[textField] as string) ?? '')}
        />
      </div>
    );
  };

  const renderItems = (finalData: JsonData) => finalData.filter(shouldRenderOption).map(renderItem);

  return (
    <div className="grid gap-2 py-1">
      {label && name && (
        <div className="flex">
          <FieldLabel label={label} name={name} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <div className={containerStyles}>{data && renderItems(data)}</div>
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};

export default RadioGroup;
